import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, LogOut, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { SEO } from "@/lib/seo";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const LEAD_STATUSES = ["New", "Contacted", "Consultation", "Application", "Closed"];
const GUIDE_CATEGORIES = ["Universities", "Applications", "Scholarships", "Student Life", "Visa & Preparation", "Living in Australia"];

const RESOURCES = {
  universities: {
    label: "Universities",
    columns: ["name", "city", "state", "group", "status"],
    fields: [
      { name: "name", label: "Name", type: "text", required: true },
      { name: "slug", label: "Slug (auto if blank)", type: "text" },
      { name: "city", label: "City", type: "text" },
      { name: "state", label: "State", type: "text" },
      { name: "country", label: "Country", type: "text" },
      { name: "group", label: "University group", type: "text" },
      { name: "descriptor", label: "Short descriptor", type: "text" },
      { name: "image", label: "Hero image URL", type: "text" },
      { name: "overview", label: "Overview", type: "textarea" },
      { name: "location_text", label: "Location", type: "textarea" },
      { name: "reputation", label: "Global reputation", type: "textarea" },
      { name: "popular_study_areas", label: "Popular study areas (comma separated)", type: "array" },
      { name: "entry_requirements", label: "Entry requirements", type: "textarea" },
      { name: "tuition_estimate", label: "Estimated tuition", type: "textarea" },
      { name: "scholarships_text", label: "Scholarships", type: "textarea" },
      { name: "campus_experience", label: "Campus experience", type: "textarea" },
      { name: "accommodation", label: "Accommodation", type: "textarea" },
      { name: "international_support", label: "International student support", type: "textarea" },
      { name: "why_consider", label: "Why consider this university?", type: "textarea" },
      { name: "faqs", label: "FAQs (JSON: [{\"q\":\"...\",\"a\":\"...\"}])", type: "json" },
      { name: "seo_title", label: "SEO title", type: "text" },
      { name: "seo_description", label: "SEO description", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: ["published", "draft"] },
    ],
  },
  scholarships: {
    label: "Scholarships",
    columns: ["name", "university_name", "study_level", "deadline", "status"],
    fields: [
      { name: "name", label: "Scholarship name", type: "text", required: true },
      { name: "university_name", label: "University", type: "text" },
      { name: "study_level", label: "Study level", type: "text" },
      { name: "eligibility", label: "Eligibility", type: "textarea" },
      { name: "value", label: "Value", type: "text" },
      { name: "deadline", label: "Deadline", type: "text" },
      { name: "learn_more_url", label: "Learn more URL", type: "text" },
      { name: "seo_title", label: "SEO title", type: "text" },
      { name: "seo_description", label: "SEO description", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: ["published", "draft"] },
    ],
  },
  guides: {
    label: "Guides",
    columns: ["title", "category", "status", "published_at"],
    fields: [
      { name: "title", label: "Title", type: "text", required: true },
      { name: "slug", label: "Slug (auto if blank)", type: "text" },
      { name: "category", label: "Category", type: "select", options: GUIDE_CATEGORIES },
      { name: "excerpt", label: "Excerpt", type: "textarea" },
      { name: "content", label: "Content (blank line between paragraphs)", type: "textarea", rows: 12 },
      { name: "featured_image", label: "Featured image URL", type: "text" },
      { name: "read_time", label: "Read time", type: "text" },
      { name: "seo_title", label: "SEO title", type: "text" },
      { name: "seo_description", label: "SEO description", type: "textarea" },
      { name: "featured", label: "Featured", type: "checkbox" },
      { name: "status", label: "Status", type: "select", options: ["published", "draft"] },
    ],
  },
};

const inputCls = "rounded-none border-input focus-visible:ring-gold bg-white";

function ContentManager({ resource }) {
  const cfg = RESOURCES[resource];
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = () => api.get(`/admin/${resource}`).then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, [resource]);

  const openNew = () => {
    const init = {};
    cfg.fields.forEach((f) => {
      init[f.name] = f.type === "array" ? "" : f.type === "json" ? "" : f.type === "checkbox" ? false : f.type === "select" ? (f.options?.[0] || "") : "";
    });
    setForm(init);
    setEditing("new");
  };

  const openEdit = (item) => {
    const init = {};
    cfg.fields.forEach((f) => {
      const v = item[f.name];
      if (f.type === "array") init[f.name] = Array.isArray(v) ? v.join(", ") : "";
      else if (f.type === "json") init[f.name] = Array.isArray(v) && v.length ? JSON.stringify(v, null, 2) : "";
      else if (f.type === "checkbox") init[f.name] = !!v;
      else init[f.name] = v ?? "";
    });
    setForm(init);
    setEditing(item.id);
  };

  const save = async () => {
    const payload = {};
    for (const f of cfg.fields) {
      let v = form[f.name];
      if (f.type === "array") v = String(v).split(",").map((s) => s.trim()).filter(Boolean);
      if (f.type === "json") {
        if (!String(v).trim()) { v = []; }
        else {
          try { v = JSON.parse(v); } catch { toast.error(`Invalid JSON in ${f.label}`); return; }
        }
      }
      payload[f.name] = v;
    }
    try {
      if (editing === "new") await api.post(`/admin/${resource}`, payload);
      else await api.put(`/admin/${resource}/${editing}`, payload);
      toast.success("Saved.");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this entry? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/${resource}/${id}`);
      toast.success("Deleted.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid={`admin-${resource}`}>
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-3xl font-medium text-navy">{cfg.label}</h2>
        <button data-testid={`admin-${resource}-new`} onClick={openNew} className="inline-flex items-center gap-2 bg-navy text-cream px-5 py-2.5 text-sm font-semibold hover:bg-gold hover:text-navy transition-colors">
          <Plus className="h-4 w-4" /> New
        </button>
      </div>

      {editing !== null && (
        <div className="fixed inset-0 z-50 bg-navy/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4" data-testid="admin-editor-modal">
          <div className="bg-cream border border-border w-full max-w-3xl p-8 relative">
            <button data-testid="admin-editor-close" onClick={() => setEditing(null)} className="absolute top-4 right-4 p-2 text-navy/50 hover:text-navy"><X className="h-5 w-5" /></button>
            <h3 className="font-serif text-2xl font-medium text-navy">{editing === "new" ? "New" : "Edit"} {cfg.label.slice(0, -1)}</h3>
            <div className="mt-8 grid sm:grid-cols-2 gap-5">
              {cfg.fields.map((f) => (
                <div key={f.name} className={f.type === "textarea" || f.type === "json" || f.type === "checkbox" ? "sm:col-span-2" : ""}>
                  {f.type === "checkbox" ? (
                    <label className="flex items-center gap-3 text-sm text-navy/80">
                      <input type="checkbox" data-testid={`field-${f.name}`} checked={!!form[f.name]} onChange={(e) => setForm({ ...form, [f.name]: e.target.checked })} className="h-4 w-4 accent-[#0A192F]" />
                      {f.label}
                    </label>
                  ) : (
                    <>
                      <Label className="text-xs uppercase tracking-[0.15em] text-navy/50">{f.label}</Label>
                      {f.type === "textarea" || f.type === "json" ? (
                        <Textarea data-testid={`field-${f.name}`} rows={f.rows || 4} value={form[f.name] ?? ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} className={`mt-1.5 ${inputCls} font-mono text-sm`} />
                      ) : f.type === "select" ? (
                        <Select value={form[f.name] || ""} onValueChange={(v) => setForm({ ...form, [f.name]: v })}>
                          <SelectTrigger data-testid={`field-${f.name}`} className={`mt-1.5 ${inputCls}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : (
                        <Input data-testid={`field-${f.name}`} value={form[f.name] ?? ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} className={`mt-1.5 ${inputCls}`} />
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
            <button data-testid="admin-editor-save" onClick={save} className="mt-8 bg-navy text-cream px-10 py-3.5 text-sm font-semibold hover:bg-gold hover:text-navy transition-colors">Save</button>
          </div>
        </div>
      )}

      <div className="mt-8 overflow-x-auto border border-border bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-sand">
              {cfg.columns.map((c) => <th key={c} className="text-left px-5 py-3.5 text-xs uppercase tracking-[0.15em] text-navy/50 font-semibold">{c.replace(/_/g, " ")}</th>)}
              <th className="px-5 py-3.5" />
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-b border-border last:border-0 hover:bg-sand/50 transition-colors" data-testid={`admin-row-${item.id}`}>
                {cfg.columns.map((c) => (
                  <td key={c} className="px-5 py-4 text-navy/80 max-w-[220px] truncate">
                    {c === "status" ? (
                      <span className={`px-2.5 py-1 text-xs font-semibold ${item[c] === "published" ? "bg-navy text-cream" : "bg-gold/20 text-navy"}`}>{item[c]}</span>
                    ) : String(item[c] ?? "—").slice(0, 60)}
                  </td>
                ))}
                <td className="px-5 py-4 whitespace-nowrap text-right">
                  <button data-testid={`edit-${item.id}`} onClick={() => openEdit(item)} className="p-2 text-navy/50 hover:text-navy"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`delete-${item.id}`} onClick={() => remove(item.id)} className="p-2 text-navy/50 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <p className="px-5 py-10 text-center text-navy/40 text-sm">No entries yet.</p>}
      </div>
    </div>
  );
}

function LeadsPanel() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("");
  const [statusEdit, setStatusEdit] = useState(null);
  const [editStatus, setEditStatus] = useState("New");
  const [editNote, setEditNote] = useState("");
  const load = () => api.get("/admin/leads", { params: filter ? { status: filter } : {} }).then((r) => setLeads(r.data)).catch(() => {});
  useEffect(() => { load(); }, [filter]);

  const openStatus = (l) => {
    setStatusEdit(l);
    setEditStatus(l.status);
    setEditNote("");
  };

  const saveStatus = async () => {
    try {
      await api.patch(`/admin/leads/${statusEdit.id}`, { status: editStatus, note: editNote });
      toast.success("Status updated — the student has been emailed.");
      setStatusEdit(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-leads">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="font-serif text-3xl font-medium text-navy">Leads</h2>
        <Select value={filter || "__all__"} onValueChange={(v) => setFilter(v === "__all__" ? "" : v)}>
          <SelectTrigger data-testid="leads-status-filter" className="w-48 rounded-none border-input"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All statuses</SelectItem>
            {LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="mt-8 overflow-x-auto border border-border bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-sand">
              {["Name", "Email", "WhatsApp", "Country", "Study interest", "Intake", "Date", "Status"].map((h) => (
                <th key={h} className="text-left px-5 py-3.5 text-xs uppercase tracking-[0.15em] text-navy/50 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} className="border-b border-border last:border-0" data-testid={`lead-row-${l.id}`}>
                <td className="px-5 py-4 text-navy font-medium">{l.name}</td>
                <td className="px-5 py-4 text-navy/70">{l.email}</td>
                <td className="px-5 py-4 text-navy/70">{l.whatsapp || "—"}</td>
                <td className="px-5 py-4 text-navy/70">{l.country || "—"}</td>
                <td className="px-5 py-4 text-navy/70 max-w-[160px] truncate">{l.study_interest || "—"}</td>
                <td className="px-5 py-4 text-navy/70">{l.intake || "—"}</td>
                <td className="px-5 py-4 text-navy/50 text-xs">{new Date(l.created_at).toLocaleDateString()}</td>
                <td className="px-5 py-4">
                  <button
                    data-testid={`lead-status-${l.id}`}
                    onClick={() => openStatus(l)}
                    className={`px-3 py-1.5 text-xs font-semibold transition-colors hover:opacity-80 ${l.status === "New" ? "bg-gold/20 text-navy" : "bg-navy text-cream"}`}
                  >
                    {l.status}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {leads.length === 0 && <p className="px-5 py-10 text-center text-navy/40 text-sm">No leads yet.</p>}
      </div>

      {statusEdit && (
        <div className="fixed inset-0 z-50 bg-navy/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-16 px-4" data-testid="status-edit-modal">
          <div className="bg-cream border border-border w-full max-w-lg p-8 relative">
            <button data-testid="status-edit-close" onClick={() => setStatusEdit(null)} className="absolute top-4 right-4 p-2 text-navy/50 hover:text-navy"><X className="h-5 w-5" /></button>
            <h3 className="font-serif text-2xl font-medium text-navy">Update status — {statusEdit.name}</h3>
            <p className="mt-2 text-xs text-navy/50">The student will receive an email about this change.</p>
            <div className="mt-6 space-y-5">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-navy/50">New status</Label>
                <Select value={editStatus} onValueChange={setEditStatus}>
                  <SelectTrigger data-testid="status-edit-select" className="mt-1.5 rounded-none border-input bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-navy/50">Note to student (optional, shown on their timeline)</Label>
                <Textarea data-testid="status-edit-note" rows={4} value={editNote} onChange={(e) => setEditNote(e.target.value)} placeholder="e.g. We discussed Go8 options; shortlist sent by email." className="mt-1.5 rounded-none border-input bg-white" />
              </div>
              <button data-testid="status-edit-save" onClick={saveStatus} className="bg-navy text-cream px-10 py-3.5 text-sm font-semibold hover:bg-gold hover:text-navy transition-colors">Save & notify student</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MessagesPanel() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/contacts").then((r) => setItems(r.data)).catch(() => {}); }, []);
  return (
    <div data-testid="admin-messages">
      <h2 className="font-serif text-3xl font-medium text-navy">Contact Messages</h2>
      <div className="mt-8 space-y-4">
        {items.map((m) => (
          <div key={m.id} className="bg-white border border-border p-6" data-testid={`message-${m.id}`}>
            <div className="flex justify-between gap-4">
              <p className="font-semibold text-navy">{m.name} <span className="font-normal text-navy/50">— {m.email}</span></p>
              <p className="text-xs text-navy/40">{new Date(m.created_at).toLocaleDateString()}</p>
            </div>
            <p className="mt-3 text-sm text-navy/70 leading-relaxed">{m.message}</p>
          </div>
        ))}
        {items.length === 0 && <p className="text-center text-navy/40 text-sm py-10 bg-white border border-border">No messages yet.</p>}
      </div>
    </div>
  );
}

function ToolkitPanel() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/toolkit-downloads").then((r) => setItems(r.data)).catch(() => {}); }, []);
  return (
    <div data-testid="admin-toolkit">
      <h2 className="font-serif text-3xl font-medium text-navy">Toolkit Downloads</h2>
      <div className="mt-8 overflow-x-auto border border-border bg-white">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border bg-sand">
            {["Name", "Email", "Date"].map((h) => <th key={h} className="text-left px-5 py-3.5 text-xs uppercase tracking-[0.15em] text-navy/50 font-semibold">{h}</th>)}
          </tr></thead>
          <tbody>
            {items.map((t) => (
              <tr key={t.id} className="border-b border-border last:border-0" data-testid={`toolkit-row-${t.id}`}>
                <td className="px-5 py-4 text-navy font-medium">{t.name}</td>
                <td className="px-5 py-4 text-navy/70">{t.email}</td>
                <td className="px-5 py-4 text-navy/50 text-xs">{new Date(t.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <p className="px-5 py-10 text-center text-navy/40 text-sm">No downloads yet.</p>}
      </div>
    </div>
  );
}

const TABS = [
  { key: "leads", label: "Leads" },
  { key: "universities", label: "Universities" },
  { key: "scholarships", label: "Scholarships" },
  { key: "guides", label: "Guides" },
  { key: "messages", label: "Messages" },
  { key: "toolkit", label: "Toolkit" },
];

export default function AdminDashboard() {
  const [user, setUser] = useState(undefined);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("leads");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => { setUser(r.data); return api.get("/admin/stats"); })
      .then((r) => setStats(r.data))
      .catch(() => { setUser(null); navigate("/admin/login"); });
  }, [navigate]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  const statCards = useMemo(() => stats ? [
    { label: "Total leads", value: stats.leads },
    { label: "New leads", value: stats.new_leads },
    { label: "Universities", value: stats.universities },
    { label: "Scholarships", value: stats.scholarships },
    { label: "Guides", value: stats.guides },
    { label: "Toolkit downloads", value: stats.toolkit_downloads },
  ] : [], [stats]);

  if (user === undefined) return <div className="min-h-screen bg-cream" />;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-cream">
      <SEO title="Vitaliah Admin" description="Admin dashboard" />
      <header className="bg-navy text-cream border-b border-navy-border">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 h-20 flex items-center justify-between">
          <p className="font-serif text-2xl font-medium">Vitaliah <span className="italic text-gold">Admin</span></p>
          <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 text-sm text-cream/70 hover:text-gold transition-colors">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </header>
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-12">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12" data-testid="admin-stats">
            {statCards.map((s) => (
              <div key={s.label} className="bg-white border border-border p-5">
                <p className="font-serif text-3xl text-navy">{s.value}</p>
                <p className="mt-1 text-xs uppercase tracking-[0.15em] text-navy/50">{s.label}</p>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-2 mb-10" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-5 py-2.5 text-sm font-medium border transition-colors ${tab === t.key ? "bg-navy text-cream border-navy" : "border-border text-navy/70 hover:border-gold"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {tab === "leads" && <LeadsPanel />}
        {tab === "messages" && <MessagesPanel />}
        {tab === "toolkit" && <ToolkitPanel />}
        {["universities", "scholarships", "guides"].includes(tab) && <ContentManager key={tab} resource={tab} />}
      </div>
    </div>
  );
}
